import { useEffect, useState, useCallback } from 'react';
import { useOutletContext, Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  getCalls, getComplianceFilters, getDepartments,
  overrideCallCompliance, qaReviewCall, bulkQAReviewCalls,
  getAlertsCategoryStats, getQAActivityLog, exportQAActivityLog,
  getQAReviewedCalls, getMarkedCompliantCalls, rideBackCallStatus
} from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { 
  AlertTriangle, 
  AlertCircle, 
  AlertOctagon,
  ChevronRight,
  ChevronLeft,
  Shield,
  ShieldAlert,
  ShieldX,
  ShieldCheck,
  Search,
  Calendar,
  Filter,
  Phone,
  Star,
  Clock,
  TrendingDown,
  X,
  ExternalLink,
  CheckCircle2,
  Edit,
  MoreVertical,
  ThumbsUp,
  ThumbsDown,
  Loader2,
  Eye,
  RefreshCw,
  ClipboardCheck,
  MessageSquare,
  Download,
  History,
  User,
  FileText,
  Undo2
} from 'lucide-react';

const CALL_TYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'introduction', label: 'Introduction' },
  { value: 'screening', label: 'Screening' },
  { value: 'interview', label: 'Interview' },
  { value: 'followup', label: 'Follow-up' },
  { value: 'client', label: 'Client' },
  { value: 'voicemail', label: 'Voicemail' },
  { value: 'other', label: 'Other' },
];

const ALERT_LEVELS = [
  { value: 'all', label: 'All Levels' },
  { value: 'critical', label: 'Critical' },
  { value: 'medium', label: 'Medium' },
  { value: 'improvement', label: 'Improvement' },
  { value: 'good', label: 'Good' },
];

const CLASSIFICATIONS = [
  { value: 'all', label: 'All Classifications' },
  { value: 'best', label: 'Best' },
  { value: 'good', label: 'Good' },
  { value: 'improvement', label: 'Improvement' },
  { value: 'high_alert', label: 'High Alert' },
];

// Generate month options for filter
const getMonthOptions = () => {
  const months = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      value: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
      label: date.toLocaleString('default', { month: 'long', year: 'numeric' })
    });
  }
  return months;
};

const MONTH_OPTIONS = getMonthOptions();

// ==================== CALL OVERRIDE DIALOG ====================
function CallOverrideDialog({ open, onClose, call, onSubmit }) {
  const [overrideReason, setOverrideReason] = useState('');
  const [qaNotes, setQaNotes] = useState('');
  const [markAsCompliant, setMarkAsCompliant] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setOverrideReason('');
      setQaNotes('');
      setMarkAsCompliant(true);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!overrideReason.trim() || overrideReason.trim().length < 5) {
      toast.error('Override reason is required (minimum 5 characters)');
      return;
    }
    
    setLoading(true);
    try {
      await onSubmit(call.id, {
        override_reason: overrideReason,
        qa_notes: qaNotes || null,
        mark_compliant: markAsCompliant
      });
      toast.success(`Call ${call.id.slice(-6)} ${markAsCompliant ? 'marked as compliant' : 'overridden'}`);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Override failed');
    } finally {
      setLoading(false);
    }
  };

  if (!call) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="h-5 w-5 text-purple-500" />
            Override Call Compliance
          </DialogTitle>
          <DialogDescription>
            {call.user?.name || 'Unknown'} - {new Date(call.call_date).toLocaleDateString()}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Call Info */}
          <div className="p-3 bg-muted/50 rounded-lg grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-muted-foreground">Score:</span>
              <span className="ml-2 font-medium">{call.average_score?.toFixed(1) || 'N/A'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Alert Level:</span>
              <Badge variant="outline" className="ml-2 capitalize">{call.alert_level}</Badge>
            </div>
            <div>
              <span className="text-muted-foreground">Classification:</span>
              <Badge variant="outline" className="ml-2 capitalize">{call.classification?.replace('_', ' ')}</Badge>
            </div>
            <div>
              <span className="text-muted-foreground">Has Notes:</span>
              <span className="ml-2">{call.has_notes ? 'Yes' : 'No'}</span>
            </div>
          </div>

          {/* Compliance Toggle */}
          <div className="space-y-2">
            <Label>Mark as Compliant?</Label>
            <div className="flex gap-3">
              <Button
                type="button"
                variant={markAsCompliant ? 'default' : 'outline'}
                onClick={() => setMarkAsCompliant(true)}
                className="flex-1 gap-2"
              >
                <ThumbsUp className="h-4 w-4" />
                Yes, Compliant
              </Button>
              <Button
                type="button"
                variant={!markAsCompliant ? 'destructive' : 'outline'}
                onClick={() => setMarkAsCompliant(false)}
                className="flex-1 gap-2"
              >
                <ThumbsDown className="h-4 w-4" />
                No, Non-Compliant
              </Button>
            </div>
          </div>

          {/* Override Reason */}
          <div className="space-y-2">
            <Label>Override Reason *</Label>
            <Textarea
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              placeholder="Explain why you're overriding this call's compliance status..."
              rows={3}
            />
          </div>

          {/* QA Notes */}
          <div className="space-y-2">
            <Label>QA Notes (Optional)</Label>
            <Textarea
              value={qaNotes}
              onChange={(e) => setQaNotes(e.target.value)}
              placeholder="Add any additional notes..."
              rows={2}
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading || !overrideReason.trim()}
            className={markAsCompliant ? 'bg-green-600 hover:bg-green-700' : ''}
          >
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Confirm Override
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== BULK OVERRIDE DIALOG ====================
function BulkOverrideDialog({ open, onClose, selectedCount, onConfirm }) {
  const [markAsCompliant, setMarkAsCompliant] = useState(true);
  const [overrideReason, setOverrideReason] = useState('');
  const [qaNotes, setQaNotes] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setMarkAsCompliant(true);
      setOverrideReason('');
      setQaNotes('');
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!overrideReason.trim() || overrideReason.trim().length < 5) {
      toast.error('Override reason is required (minimum 5 characters)');
      return;
    }
    
    setLoading(true);
    try {
      await onConfirm({
        mark_compliant: markAsCompliant,
        override_reason: overrideReason,
        qa_notes: qaNotes || null
      });
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk override failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="h-5 w-5 text-purple-500" />
            Bulk Override Compliance
          </DialogTitle>
          <DialogDescription>
            Override compliance for {selectedCount} selected call{selectedCount > 1 ? 's' : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Warning */}
          <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800 dark:text-amber-200">
              This action will override the compliance status for all selected calls. This action is tracked and cannot be undone.
            </div>
          </div>

          {/* Compliance Toggle */}
          <div className="space-y-2">
            <Label>New Compliance Status</Label>
            <div className="flex gap-3">
              <Button
                type="button"
                variant={markAsCompliant ? 'default' : 'outline'}
                onClick={() => setMarkAsCompliant(true)}
                className="flex-1 gap-2"
              >
                <ThumbsUp className="h-4 w-4" />
                Mark as Compliant
              </Button>
              <Button
                type="button"
                variant={!markAsCompliant ? 'destructive' : 'outline'}
                onClick={() => setMarkAsCompliant(false)}
                className="flex-1 gap-2"
              >
                <ThumbsDown className="h-4 w-4" />
                Mark as Non-Compliant
              </Button>
            </div>
          </div>

          {/* Override Reason */}
          <div className="space-y-2">
            <Label>Override Reason *</Label>
            <Textarea
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              placeholder="Provide a reason for this bulk override (required)..."
              rows={3}
            />
          </div>

          {/* QA Notes */}
          <div className="space-y-2">
            <Label>QA Notes (Optional)</Label>
            <Textarea
              value={qaNotes}
              onChange={(e) => setQaNotes(e.target.value)}
              placeholder="Add any additional notes..."
              rows={2}
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading || !overrideReason.trim()}
            className={markAsCompliant ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
          >
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Override {selectedCount} Call{selectedCount > 1 ? 's' : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== QA REVIEW DIALOG ====================
function QAReviewDialog({ open, onClose, call, onSubmit }) {
  const [qaNote, setQaNote] = useState('');
  const [markCompliant, setMarkCompliant] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setQaNote('');
      setMarkCompliant(false);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!qaNote.trim() || qaNote.trim().length < 5) {
      toast.error('QA note is required (minimum 5 characters)');
      return;
    }
    
    setLoading(true);
    try {
      await onSubmit(call.id, qaNote, markCompliant);
      toast.success(`Call marked as QA Reviewed${markCompliant ? ' and Compliant' : ''}`);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'QA Review failed');
    } finally {
      setLoading(false);
    }
  };

  if (!call) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5 text-emerald-500" />
            QA Review Call
          </DialogTitle>
          <DialogDescription>
            {call.user?.name || 'Unknown'} - {new Date(call.call_date).toLocaleDateString()}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Call Info */}
          <div className="p-3 bg-muted/50 rounded-lg grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-muted-foreground">Score:</span>
              <span className="ml-2 font-medium">{call.average_score?.toFixed(1) || 'N/A'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Alert Level:</span>
              <Badge variant="outline" className="ml-2 capitalize">{call.alert_level}</Badge>
            </div>
            <div>
              <span className="text-muted-foreground">Classification:</span>
              <Badge variant="outline" className="ml-2 capitalize">{call.classification?.replace('_', ' ')}</Badge>
            </div>
            <div>
              <span className="text-muted-foreground">QA Status:</span>
              <Badge 
                variant={call.qa_reviewed ? 'default' : 'secondary'} 
                className="ml-2"
              >
                {call.qa_reviewed ? 'Reviewed' : 'Not Reviewed'}
              </Badge>
            </div>
          </div>

          {/* QA Note - Mandatory */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              QA Note *
            </Label>
            <Textarea
              value={qaNote}
              onChange={(e) => setQaNote(e.target.value)}
              placeholder="Enter your QA review notes (required)..."
              rows={4}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground">
              This note will be tagged to the call and logged in the audit trail.
            </p>
          </div>

          {/* Mark Compliant Option */}
          <div className="flex items-center justify-between p-3 border rounded-lg bg-emerald-500/5 dark:bg-emerald-500/10">
            <div>
              <Label className="text-sm font-medium">Mark as Compliant?</Label>
              <p className="text-xs text-muted-foreground">
                If enabled, this call will be removed from alerts
              </p>
            </div>
            <Switch 
              checked={markCompliant} 
              onCheckedChange={setMarkCompliant}
              className="data-[state=checked]:bg-emerald-500"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading || !qaNote.trim() || qaNote.trim().length < 5}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            <ClipboardCheck className="h-4 w-4 mr-2" />
            Submit QA Review
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== BULK QA REVIEW DIALOG ====================
function BulkQAReviewDialog({ open, onClose, selectedCount, onConfirm }) {
  const [qaNote, setQaNote] = useState('');
  const [markCompliant, setMarkCompliant] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setQaNote('');
      setMarkCompliant(false);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!qaNote.trim() || qaNote.trim().length < 5) {
      toast.error('QA note is required (minimum 5 characters)');
      return;
    }
    
    setLoading(true);
    try {
      await onConfirm(qaNote, markCompliant);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk QA Review failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5 text-emerald-500" />
            Bulk QA Review
          </DialogTitle>
          <DialogDescription>
            QA Review {selectedCount} selected call{selectedCount > 1 ? 's' : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Info */}
          <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg flex items-start gap-2">
            <ClipboardCheck className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-emerald-800 dark:text-emerald-200">
              All selected calls will be marked as QA Reviewed with the same note. This action is tracked in the audit log.
            </div>
          </div>

          {/* QA Note - Mandatory */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              QA Note *
            </Label>
            <Textarea
              value={qaNote}
              onChange={(e) => setQaNote(e.target.value)}
              placeholder="Enter your QA review notes for all selected calls (required)..."
              rows={4}
              className="resize-none"
            />
          </div>

          {/* Mark Compliant Option */}
          <div className="flex items-center justify-between p-3 border rounded-lg bg-emerald-500/5 dark:bg-emerald-500/10">
            <div>
              <Label className="text-sm font-medium">Mark all as Compliant?</Label>
              <p className="text-xs text-muted-foreground">
                If enabled, all selected calls will be removed from alerts
              </p>
            </div>
            <Switch 
              checked={markCompliant} 
              onCheckedChange={setMarkCompliant}
              className="data-[state=checked]:bg-emerald-500"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading || !qaNote.trim() || qaNote.trim().length < 5}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            QA Review {selectedCount} Call{selectedCount > 1 ? 's' : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== RIDE BACK DIALOG ====================
function RideBackDialog({ open, onClose, call, onSubmit }) {
  const [newStatus, setNewStatus] = useState('pending_review');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setNewStatus('pending_review');
      setReason('');
      setNotes('');
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!reason.trim() || reason.trim().length < 5) {
      toast.error('Reason is required (minimum 5 characters)');
      return;
    }
    
    setLoading(true);
    try {
      await onSubmit(call.id, newStatus, reason, notes || null);
      toast.success(`Call status reverted to "${newStatus.replace(/_/g, ' ')}"`);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revert status');
    } finally {
      setLoading(false);
    }
  };

  if (!call) return null;

  const statusOptions = [
    { value: 'pending_review', label: 'Pending Review', description: 'Call needs to be reviewed again' },
    { value: 'qa_not_reviewed', label: 'Not Reviewed', description: 'Remove QA reviewed status' },
    { value: 'needs_attention', label: 'Needs Attention', description: 'Flag for special attention' }
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Undo2 className="h-5 w-5 text-amber-500" />
            Ride Back - Revert Status
          </DialogTitle>
          <DialogDescription>
            Revert the QA review status for {call.user?.name || 'this call'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Call Info */}
          <div className="p-3 bg-muted/50 rounded-lg grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-muted-foreground">Current Status:</span>
              <Badge 
                variant={call.qa_reviewed ? 'default' : 'secondary'} 
                className="ml-2"
              >
                {call.qa_reviewed ? 'QA Reviewed' : 'Not Reviewed'}
              </Badge>
            </div>
            <div>
              <span className="text-muted-foreground">Compliant:</span>
              <Badge 
                variant={call.marked_compliant ? 'default' : 'secondary'} 
                className={`ml-2 ${call.marked_compliant ? 'bg-emerald-500/20 text-emerald-700' : ''}`}
              >
                {call.marked_compliant ? 'Yes' : 'No'}
              </Badge>
            </div>
          </div>

          {/* Status Selection */}
          <div className="space-y-2">
            <Label>Revert to Status *</Label>
            <Select value={newStatus} onValueChange={setNewStatus}>
              <SelectTrigger>
                <SelectValue placeholder="Select new status" />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    <div className="flex flex-col">
                      <span className="font-medium">{opt.label}</span>
                      <span className="text-xs text-muted-foreground">{opt.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Reason - Mandatory */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Reason for Revert *
            </Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explain why you're reverting this call's status..."
              rows={3}
            />
          </div>

          {/* Additional Notes - Optional */}
          <div className="space-y-2">
            <Label>Additional Notes (Optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional context..."
              rows={2}
            />
          </div>

          {/* Warning */}
          <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800 dark:text-amber-200">
              This will clear the QA reviewed status and any compliance marking. The call will reappear in the alerts queue.
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading || !reason.trim() || reason.trim().length < 5}
            className="bg-amber-600 hover:bg-amber-700"
          >
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            <Undo2 className="h-4 w-4 mr-2" />
            Revert Status
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== MAIN COMPONENT ====================
export default function AdminAlerts() {
  // Handle both legacy admin and SaaS tenant contexts
  const outletContext = useOutletContext() || {};
  const { tenant, adminUrl, user } = outletContext;
  const tenantId = tenant?.id || tenant?.tenant_id;
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  
  // Build base URL for navigation
  const getBaseUrl = () => {
    if (adminUrl) return `/admin/${adminUrl}`;
    return '/dashboard';
  };
  const baseUrl = getBaseUrl();
  
  // Alert counts
  const [criticalCalls, setCriticalCalls] = useState([]);
  const [mediumCalls, setMediumCalls] = useState([]);
  const [improvementCalls, setImprovementCalls] = useState([]);
  const [nonComplianceCalls, setNonComplianceCalls] = useState([]);
  
  // Filters and data
  const [complianceFilters, setComplianceFilters] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filteredCalls, setFilteredCalls] = useState([]);
  
  // Selection state
  const [selectedCalls, setSelectedCalls] = useState([]);
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [bulkOverrideDialogOpen, setBulkOverrideDialogOpen] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  
  // QA Review state
  const [qaReviewDialogOpen, setQaReviewDialogOpen] = useState(false);
  const [bulkQAReviewDialogOpen, setBulkQAReviewDialogOpen] = useState(false);
  const [selectedQAReviewCall, setSelectedQAReviewCall] = useState(null);
  
  // Ride Back state
  const [rideBackDialogOpen, setRideBackDialogOpen] = useState(false);
  const [selectedRideBackCall, setSelectedRideBackCall] = useState(null);
  
  // QA Reviewed / Marked Compliant tabs data
  const [qaReviewedCalls, setQaReviewedCalls] = useState([]);
  const [markedCompliantCalls, setMarkedCompliantCalls] = useState([]);
  const [qaReviewedTotal, setQaReviewedTotal] = useState(0);
  const [markedCompliantTotal, setMarkedCompliantTotal] = useState(0);
  
  // QA Category Stats (for cards)
  const [categoryStats, setCategoryStats] = useState({
    critical: { total: 0, qa_reviewed: 0, qa_not_reviewed: 0 },
    medium: { total: 0, qa_reviewed: 0, qa_not_reviewed: 0 },
    improvement: { total: 0, qa_reviewed: 0, qa_not_reviewed: 0 },
    non_compliance: { total: 0, qa_reviewed: 0, qa_not_reviewed: 0 }
  });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(100);
  const [totalPages, setTotalPages] = useState(1);
  
  // Activity Log state
  const [activityLogOpen, setActivityLogOpen] = useState(false);
  const [activityLog, setActivityLog] = useState([]);
  const [activityLogLoading, setActivityLogLoading] = useState(false);
  const [activityLogPage, setActivityLogPage] = useState(1);
  const [activityLogTotal, setActivityLogTotal] = useState(0);
  const [activityLogTotalPages, setActivityLogTotalPages] = useState(0);
  const [activityLogFilters, setActivityLogFilters] = useState({
    action_type: '',
    auditor_id: '',
    date_from: '',
    date_to: ''
  });
  const [activityFilterOptions, setActivityFilterOptions] = useState({
    action_types: [],
    auditors: []
  });
  const [exportLoading, setExportLoading] = useState(false);
  
  // Active filter state
  const [activeTab, setActiveTab] = useState('critical');
  const [filters, setFilters] = useState({
    search: '',
    compliance_filter: 'all',
    call_type: 'all',
    department_id: 'all',
    alert_level: 'all',
    classification: 'all',
    qa_status: 'all',  // New QA status filter: all, qa_reviewed, qa_not_reviewed
    date_from: '',
    date_to: '',
    month: 'all'
  });

  useEffect(() => {
    loadInitialData();
  }, [tenantId]);

  useEffect(() => {
    filterCalls();
  }, [filters, criticalCalls, mediumCalls, improvementCalls, nonComplianceCalls]);

  // Clear selection when tab changes (now handled by handleTabChange)
  useEffect(() => {
    setSelectedCalls([]);
  }, [activeTab]);

  const loadInitialData = async () => {
    setIsLoading(true);
    try {
      const baseParams = tenantId ? { tenant_id: tenantId } : {};
      
      // First, get category stats (this gives us the REAL totals)
      const [compFilters, depts, statsResp] = await Promise.all([
        getComplianceFilters().catch(() => ({ data: [] })),
        getDepartments(tenantId),
        getAlertsCategoryStats(tenantId).catch(() => ({ data: {} }))
      ]);
      
      setComplianceFilters(compFilters.data || []);
      setDepartments(depts.data || []);
      
      // Set category stats from API - these are the REAL totals
      if (statsResp.data) {
        setCategoryStats({
          critical: statsResp.data.critical || { total: 0, qa_reviewed: 0, qa_not_reviewed: 0 },
          medium: statsResp.data.medium || { total: 0, qa_reviewed: 0, qa_not_reviewed: 0 },
          improvement: statsResp.data.improvement || { total: 0, qa_reviewed: 0, qa_not_reviewed: 0 },
          non_compliance: statsResp.data.non_compliance || { total: 0, qa_reviewed: 0, qa_not_reviewed: 0 }
        });
      }
      
      // Load QA Reviewed and Marked Compliant counts
      try {
        const [reviewedResp, compliantResp] = await Promise.all([
          getQAReviewedCalls({ tenant_id: tenantId, page: 1, page_size: 1 }),
          getMarkedCompliantCalls({ tenant_id: tenantId, page: 1, page_size: 1 })
        ]);
        setQaReviewedTotal(reviewedResp.data.total || 0);
        setMarkedCompliantTotal(compliantResp.data.total || 0);
      } catch (err) {
        console.error('Failed to load reviewed/compliant counts:', err);
      }
      
      // Load the first page for current tab
      await loadTabData(activeTab, 1);
      
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Load data for a specific tab with pagination
  const loadTabData = async (tab, page = 1) => {
    try {
      const baseParams = tenantId ? { tenant_id: tenantId } : {};
      const params = {
        ...baseParams,
        page,
        page_size: pageSize
      };
      
      let response;
      switch (tab) {
        case 'critical':
          response = await getCalls({ ...params, alert_level: 'critical' });
          setCriticalCalls(response.data.calls || []);
          break;
        case 'medium':
          response = await getCalls({ ...params, alert_level: 'medium' });
          setMediumCalls(response.data.calls || []);
          break;
        case 'improvement':
          response = await getCalls({ ...params, alert_level: 'improvement' });
          setImprovementCalls(response.data.calls || []);
          break;
        case 'non-compliance':
          response = await getCalls({ ...params, classification: 'high_alert' });
          setNonComplianceCalls(response.data.calls || []);
          break;
        case 'qa-reviewed':
          response = await getQAReviewedCalls({ ...params });
          setQaReviewedCalls(response.data.calls || []);
          setQaReviewedTotal(response.data.total || 0);
          break;
        case 'marked-compliant':
          response = await getMarkedCompliantCalls({ ...params });
          setMarkedCompliantCalls(response.data.calls || []);
          setMarkedCompliantTotal(response.data.total || 0);
          break;
        default:
          response = await getCalls({ ...params, alert_level: 'critical' });
          setCriticalCalls(response.data.calls || []);
      }
      
      // Update pagination info
      const total = response.data.total || 0;
      setTotalPages(Math.ceil(total / pageSize) || 1);
      setCurrentPage(page);
      
    } catch (error) {
      console.error(`Failed to load ${tab} data:`, error);
    }
  };

  // Handle tab change - load data for new tab
  const handleTabChange = async (newTab) => {
    setActiveTab(newTab);
    setCurrentPage(1);
    setSelectedCalls([]);
    setIsLoading(true);
    await loadTabData(newTab, 1);
    setIsLoading(false);
  };

  // Handle page change
  const handlePageChange = async (newPage) => {
    setIsLoading(true);
    await loadTabData(activeTab, newPage);
    setIsLoading(false);
  };

  const filterCalls = () => {
    let calls = [];
    
    switch (activeTab) {
      case 'critical':
        calls = criticalCalls;
        break;
      case 'medium':
        calls = mediumCalls;
        break;
      case 'improvement':
        calls = improvementCalls;
        break;
      case 'non-compliance':
        calls = nonComplianceCalls;
        break;
      case 'qa-reviewed':
        calls = qaReviewedCalls;
        break;
      case 'marked-compliant':
        calls = markedCompliantCalls;
        break;
      default:
        calls = criticalCalls;
    }
    
    // Apply filters
    let filtered = [...calls];
    
    if (filters.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(call => 
        call.user?.name?.toLowerCase().includes(search) ||
        call.phone_number?.toLowerCase().includes(search) ||
        call.filename?.toLowerCase().includes(search)
      );
    }
    
    if (filters.call_type !== 'all') {
      filtered = filtered.filter(call => call.call_type === filters.call_type);
    }
    
    if (filters.department_id !== 'all') {
      filtered = filtered.filter(call => call.user?.department_id === filters.department_id);
    }
    
    if (filters.alert_level !== 'all') {
      filtered = filtered.filter(call => call.alert_level === filters.alert_level);
    }
    
    if (filters.classification !== 'all') {
      filtered = filtered.filter(call => call.classification === filters.classification);
    }
    
    if (filters.compliance_filter !== 'all') {
      filtered = filtered.filter(call => {
        const alerts = call.compliance_alerts || [];
        switch (filters.compliance_filter) {
          case 'missing_branding':
            return alerts.some(a => a.type === 'missing_branding');
          case 'poor_communication':
            return alerts.some(a => a.type === 'poor_communication');
          case 'improper_voicemail':
            return alerts.some(a => a.type === 'improper_voicemail');
          case 'guideline_violation':
            return alerts.some(a => a.type === 'guideline_violation');
          default:
            return true;
        }
      });
    }
    
    // QA Status filter
    if (filters.qa_status !== 'all') {
      filtered = filtered.filter(call => {
        if (filters.qa_status === 'qa_reviewed') {
          return call.qa_reviewed === true || call.qa_status === 'qa_reviewed';
        } else if (filters.qa_status === 'qa_not_reviewed') {
          return !call.qa_reviewed && call.qa_status !== 'qa_reviewed';
        }
        return true;
      });
    }
    
    // Handle month filter
    if (filters.month !== 'all') {
      const [year, month] = filters.month.split('-');
      const startDate = new Date(year, parseInt(month) - 1, 1);
      const endDate = new Date(year, parseInt(month), 0);
      filtered = filtered.filter(call => {
        const callDate = new Date(call.call_date);
        return callDate >= startDate && callDate <= endDate;
      });
    } else {
      if (filters.date_from) {
        filtered = filtered.filter(call => 
          new Date(call.call_date) >= new Date(filters.date_from)
        );
      }
      
      if (filters.date_to) {
        filtered = filtered.filter(call => 
          new Date(call.call_date) <= new Date(filters.date_to)
        );
      }
    }
    
    setFilteredCalls(filtered);
  };

  const handleCardClick = (tab) => {
    setActiveTab(tab);
    setSelectedCalls([]);
  };

  // Handle clicking on QA status counts in cards
  const handleQAStatusClick = (tab, qaStatus) => {
    setActiveTab(tab);
    setFilters(prev => ({ ...prev, qa_status: qaStatus }));
    setSelectedCalls([]);
  };

  // Load Activity Log
  const loadActivityLog = async (page = 1) => {
    setActivityLogLoading(true);
    try {
      const params = {
        tenant_id: tenantId,
        page,
        page_size: 100
      };
      
      if (activityLogFilters.action_type) {
        params.action_type = activityLogFilters.action_type;
      }
      if (activityLogFilters.auditor_id) {
        params.auditor_id = activityLogFilters.auditor_id;
      }
      if (activityLogFilters.date_from) {
        params.date_from = activityLogFilters.date_from;
      }
      if (activityLogFilters.date_to) {
        params.date_to = activityLogFilters.date_to;
      }
      
      const response = await getQAActivityLog(params);
      setActivityLog(response.data.activities || []);
      setActivityLogTotal(response.data.total || 0);
      setActivityLogTotalPages(response.data.total_pages || 0);
      setActivityLogPage(page);
      setActivityFilterOptions({
        action_types: response.data.filters?.action_types || [],
        auditors: response.data.filters?.auditors || []
      });
    } catch (error) {
      console.error('Failed to load activity log:', error);
      toast.error('Failed to load activity log');
    } finally {
      setActivityLogLoading(false);
    }
  };

  // Open activity log dialog
  const handleOpenActivityLog = () => {
    setActivityLogOpen(true);
    loadActivityLog(1);
  };

  // Export Activity Log
  const handleExportActivityLog = async () => {
    setExportLoading(true);
    try {
      const params = { tenant_id: tenantId };
      if (activityLogFilters.action_type) params.action_type = activityLogFilters.action_type;
      if (activityLogFilters.auditor_id) params.auditor_id = activityLogFilters.auditor_id;
      if (activityLogFilters.date_from) params.date_from = activityLogFilters.date_from;
      if (activityLogFilters.date_to) params.date_to = activityLogFilters.date_to;
      
      const response = await exportQAActivityLog(params);
      
      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `qa_activity_log_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Activity log exported successfully');
    } catch (error) {
      console.error('Failed to export activity log:', error);
      toast.error('Failed to export activity log');
    } finally {
      setExportLoading(false);
    }
  };

  // Format action type for display
  const formatActionType = (action) => {
    const actionMap = {
      'qa_reviewed': 'QA Reviewed',
      'qa_reviewed_bulk': 'Bulk QA Review',
      'qa_review': 'QA Review',
      'qa_review_compliant': 'Marked Compliant',
      'override_compliance': 'Compliance Override',
      'bulk_override': 'Bulk Override',
      'reviewed_alert': 'Alert Reviewed',
      'bulk_reviewed_alerts': 'Bulk Alert Review',
      'archive_reviewed_alerts': 'Archived Alerts'
    };
    return actionMap[action] || action?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Unknown';
  };

  // Format relative time
  const formatRelativeTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      compliance_filter: 'all',
      call_type: 'all',
      department_id: 'all',
      alert_level: 'all',
      classification: 'all',
      qa_status: 'all',
      date_from: '',
      date_to: '',
      month: 'all'
    });
  };

  const clearDateFilters = () => {
    setFilters(prev => ({...prev, date_from: '', date_to: '', month: 'all'}));
  };

  // Selection handlers
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedCalls(filteredCalls.map(c => c.id));
    } else {
      setSelectedCalls([]);
    }
  };

  const handleSelectCall = (callId, checked) => {
    if (checked) {
      setSelectedCalls(prev => [...prev, callId]);
    } else {
      setSelectedCalls(prev => prev.filter(id => id !== callId));
    }
  };

  // Override handlers
  const handleSingleOverride = async (callId, data) => {
    await overrideCallCompliance(callId, {
      override_reason: data.override_reason,
      qa_notes: data.qa_notes,
      mark_compliant: data.mark_compliant,
      override_all: true
    });
    loadInitialData(); // Refresh data
  };

  const handleBulkOverride = async (data) => {
    setBulkActionLoading(true);
    try {
      // Process each call
      const promises = selectedCalls.map(callId => 
        overrideCallCompliance(callId, {
          override_reason: data.override_reason,
          qa_notes: data.qa_notes,
          mark_compliant: data.mark_compliant,
          override_all: true
        })
      );
      await Promise.all(promises);
      toast.success(`${selectedCalls.length} calls overridden successfully`);
      setSelectedCalls([]);
      loadInitialData();
    } catch (error) {
      toast.error('Some overrides failed');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleQuickMarkCompliant = async (call) => {
    try {
      await overrideCallCompliance(call.id, {
        override_reason: 'Quick mark compliant by QA auditor',
        qa_notes: null,
        mark_compliant: true,
        override_all: true
      });
      toast.success(`Call marked as compliant`);
      loadInitialData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark compliant');
    }
  };

  // QA Review handlers
  const handleQAReview = async (callId, qaNote, markCompliant) => {
    await qaReviewCall(callId, qaNote, markCompliant);
    loadInitialData(); // Refresh data
  };

  const handleBulkQAReview = async (qaNote, markCompliant) => {
    setBulkActionLoading(true);
    try {
      await bulkQAReviewCalls(selectedCalls, qaNote, markCompliant);
      toast.success(`${selectedCalls.length} calls QA Reviewed successfully`);
      setSelectedCalls([]);
      loadInitialData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk QA Review failed');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleQuickQAReview = (call) => {
    setSelectedQAReviewCall(call);
    setQaReviewDialogOpen(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  const totalAlerts = criticalCalls.length + mediumCalls.length + improvementCalls.length + nonComplianceCalls.length;

  return (
    <div className="space-y-6" data-testid="admin-alerts">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-500 to-orange-500 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-lg">
              <ShieldAlert className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Alerts & Compliance</h1>
              <p className="text-red-100 mt-1">
                {totalAlerts} total alerts requiring attention
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="secondary" 
              onClick={handleOpenActivityLog}
              className="gap-2"
              data-testid="activity-log-btn"
            >
              <History className="h-4 w-4" />
              Activity Log
            </Button>
            <Button 
              variant="secondary" 
              onClick={loadInitialData}
              className="gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Cards - With QA Status Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Critical Alerts Card */}
        <Card 
          className={`transition-all hover:shadow-lg border-2 ${
            activeTab === 'critical' ? 'border-red-500 shadow-lg ring-2 ring-red-500/30' : 'border-red-500/30 hover:border-red-500/60'
          } bg-red-500/10 dark:bg-red-500/20`}
          data-testid="critical-alerts-card"
        >
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-red-600 dark:text-red-400 font-medium">Critical Alerts</p>
                <button 
                  onClick={() => { handleCardClick('critical'); setFilters(prev => ({...prev, qa_status: 'all'})); }}
                  className="text-3xl font-bold text-red-700 dark:text-red-300 mt-1 hover:underline cursor-pointer"
                  data-testid="critical-total-count"
                >
                  {categoryStats.critical.total || criticalCalls.length}
                </button>
              </div>
              <div className="p-2 bg-red-500/20 rounded-lg">
                <AlertOctagon className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
            </div>
            {/* QA Status Breakdown */}
            <div className="flex items-center gap-3 mt-3 text-xs">
              <button 
                onClick={() => handleQAStatusClick('critical', 'qa_reviewed')}
                className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 hover:underline cursor-pointer"
                data-testid="critical-reviewed-count"
              >
                <CheckCircle2 className="h-3 w-3" />
                {categoryStats.critical.qa_reviewed}
              </button>
              <span className="text-red-600/50 dark:text-red-400/50">|</span>
              <button 
                onClick={() => handleQAStatusClick('critical', 'qa_not_reviewed')}
                className="flex items-center gap-1 text-red-600 dark:text-red-400 hover:underline cursor-pointer"
                data-testid="critical-not-reviewed-count"
              >
                <X className="h-3 w-3" />
                {categoryStats.critical.qa_not_reviewed}
              </button>
            </div>
            <p className="text-xs text-red-600/70 dark:text-red-400/70 mt-1">Requires immediate attention</p>
          </CardContent>
        </Card>

        {/* Medium Alerts Card */}
        <Card 
          className={`transition-all hover:shadow-lg border-2 ${
            activeTab === 'medium' ? 'border-amber-500 shadow-lg ring-2 ring-amber-500/30' : 'border-amber-500/30 hover:border-amber-500/60'
          } bg-amber-500/10 dark:bg-amber-500/20`}
          data-testid="medium-alerts-card"
        >
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-amber-600 dark:text-amber-400 font-medium">Medium Alerts</p>
                <button 
                  onClick={() => { handleCardClick('medium'); setFilters(prev => ({...prev, qa_status: 'all'})); }}
                  className="text-3xl font-bold text-amber-700 dark:text-amber-300 mt-1 hover:underline cursor-pointer"
                  data-testid="medium-total-count"
                >
                  {categoryStats.medium.total || mediumCalls.length}
                </button>
              </div>
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <AlertTriangle className="h-6 w-6 text-amber-600 dark:text-amber-400" />
              </div>
            </div>
            {/* QA Status Breakdown */}
            <div className="flex items-center gap-3 mt-3 text-xs">
              <button 
                onClick={() => handleQAStatusClick('medium', 'qa_reviewed')}
                className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 hover:underline cursor-pointer"
                data-testid="medium-reviewed-count"
              >
                <CheckCircle2 className="h-3 w-3" />
                {categoryStats.medium.qa_reviewed}
              </button>
              <span className="text-amber-600/50 dark:text-amber-400/50">|</span>
              <button 
                onClick={() => handleQAStatusClick('medium', 'qa_not_reviewed')}
                className="flex items-center gap-1 text-amber-600 dark:text-amber-400 hover:underline cursor-pointer"
                data-testid="medium-not-reviewed-count"
              >
                <X className="h-3 w-3" />
                {categoryStats.medium.qa_not_reviewed}
              </button>
            </div>
            <p className="text-xs text-amber-600/70 dark:text-amber-400/70 mt-1">Review within 24 hours</p>
          </CardContent>
        </Card>

        {/* Improvement Needed Card */}
        <Card 
          className={`transition-all hover:shadow-lg border-2 ${
            activeTab === 'improvement' ? 'border-yellow-500 shadow-lg ring-2 ring-yellow-500/30' : 'border-yellow-500/30 hover:border-yellow-500/60'
          } bg-yellow-500/10 dark:bg-yellow-500/20`}
          data-testid="improvement-alerts-card"
        >
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-yellow-600 dark:text-yellow-400 font-medium">Improvement Needed</p>
                <button 
                  onClick={() => { handleCardClick('improvement'); setFilters(prev => ({...prev, qa_status: 'all'})); }}
                  className="text-3xl font-bold text-yellow-700 dark:text-yellow-300 mt-1 hover:underline cursor-pointer"
                  data-testid="improvement-total-count"
                >
                  {categoryStats.improvement.total || improvementCalls.length}
                </button>
              </div>
              <div className="p-2 bg-yellow-500/20 rounded-lg">
                <AlertCircle className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
              </div>
            </div>
            {/* QA Status Breakdown */}
            <div className="flex items-center gap-3 mt-3 text-xs">
              <button 
                onClick={() => handleQAStatusClick('improvement', 'qa_reviewed')}
                className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 hover:underline cursor-pointer"
                data-testid="improvement-reviewed-count"
              >
                <CheckCircle2 className="h-3 w-3" />
                {categoryStats.improvement.qa_reviewed}
              </button>
              <span className="text-yellow-600/50 dark:text-yellow-400/50">|</span>
              <button 
                onClick={() => handleQAStatusClick('improvement', 'qa_not_reviewed')}
                className="flex items-center gap-1 text-yellow-600 dark:text-yellow-400 hover:underline cursor-pointer"
                data-testid="improvement-not-reviewed-count"
              >
                <X className="h-3 w-3" />
                {categoryStats.improvement.qa_not_reviewed}
              </button>
            </div>
            <p className="text-xs text-yellow-600/70 dark:text-yellow-400/70 mt-1">Coaching recommended</p>
          </CardContent>
        </Card>

        {/* Non-Compliance Card */}
        <Card 
          className={`transition-all hover:shadow-lg border-2 ${
            activeTab === 'non-compliance' ? 'border-purple-500 shadow-lg ring-2 ring-purple-500/30' : 'border-purple-500/30 hover:border-purple-500/60'
          } bg-purple-500/10 dark:bg-purple-500/20`}
          data-testid="non-compliance-card"
        >
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-purple-600 dark:text-purple-400 font-medium">Non-Compliance</p>
                <button 
                  onClick={() => { handleCardClick('non-compliance'); setFilters(prev => ({...prev, qa_status: 'all'})); }}
                  className="text-3xl font-bold text-purple-700 dark:text-purple-300 mt-1 hover:underline cursor-pointer"
                  data-testid="non-compliance-total-count"
                >
                  {categoryStats.non_compliance.total || nonComplianceCalls.length}
                </button>
              </div>
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <ShieldX className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
            {/* QA Status Breakdown */}
            <div className="flex items-center gap-3 mt-3 text-xs">
              <button 
                onClick={() => handleQAStatusClick('non-compliance', 'qa_reviewed')}
                className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 hover:underline cursor-pointer"
                data-testid="non-compliance-reviewed-count"
              >
                <CheckCircle2 className="h-3 w-3" />
                {categoryStats.non_compliance.qa_reviewed}
              </button>
              <span className="text-purple-600/50 dark:text-purple-400/50">|</span>
              <button 
                onClick={() => handleQAStatusClick('non-compliance', 'qa_not_reviewed')}
                className="flex items-center gap-1 text-purple-600 dark:text-purple-400 hover:underline cursor-pointer"
                data-testid="non-compliance-not-reviewed-count"
              >
                <X className="h-3 w-3" />
                {categoryStats.non_compliance.qa_not_reviewed}
              </button>
            </div>
            <p className="text-xs text-purple-600/70 dark:text-purple-400/70 mt-1">High alert classification</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-card">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Filter className="h-4 w-4" />
              <span className="text-sm font-medium">Filters:</span>
            </div>
            
            <div className="relative flex-1 min-w-[200px] max-w-[300px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by user, phone..."
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
                className="pl-10"
              />
            </div>
            
            <Select value={filters.alert_level} onValueChange={(v) => setFilters({...filters, alert_level: v})}>
              <SelectTrigger className="w-[150px]">
                <AlertTriangle className="h-4 w-4 mr-2 text-red-500" />
                <SelectValue placeholder="Alert Level" />
              </SelectTrigger>
              <SelectContent>
                {ALERT_LEVELS.map(level => (
                  <SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filters.classification} onValueChange={(v) => setFilters({...filters, classification: v})}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Classification" />
              </SelectTrigger>
              <SelectContent>
                {CLASSIFICATIONS.map(cls => (
                  <SelectItem key={cls.value} value={cls.value}>{cls.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filters.compliance_filter} onValueChange={(v) => setFilters({...filters, compliance_filter: v})}>
              <SelectTrigger className="w-[200px]">
                <ShieldAlert className="h-4 w-4 mr-2 text-amber-500" />
                <SelectValue placeholder="Compliance Issue" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Issues</SelectItem>
                <SelectItem value="missing_branding">Missing Branding</SelectItem>
                <SelectItem value="poor_communication">Poor Communication</SelectItem>
                <SelectItem value="improper_voicemail">Improper Voicemail</SelectItem>
                <SelectItem value="guideline_violation">Guideline Violation</SelectItem>
                {complianceFilters.map(filter => (
                  <SelectItem key={filter.value} value={filter.value}>
                    {filter.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* QA Status Filter */}
            <Select value={filters.qa_status} onValueChange={(v) => setFilters({...filters, qa_status: v})}>
              <SelectTrigger className="w-[170px]">
                <ClipboardCheck className="h-4 w-4 mr-2 text-emerald-500" />
                <SelectValue placeholder="QA Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="qa_reviewed">QA Reviewed</SelectItem>
                <SelectItem value="qa_not_reviewed">QA Not Reviewed</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filters.call_type} onValueChange={(v) => setFilters({...filters, call_type: v})}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Call Type" />
              </SelectTrigger>
              <SelectContent>
                {CALL_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filters.department_id} onValueChange={(v) => setFilters({...filters, department_id: v})}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Department" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map(dept => (
                  <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Date Filters Row */}
          <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-border">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Calendar className="h-4 w-4" />
              <span className="text-sm font-medium">Date Filter:</span>
            </div>
            
            <Select value={filters.month} onValueChange={(v) => setFilters({...filters, month: v, date_from: '', date_to: ''})}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select Month" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                {MONTH_OPTIONS.map(month => (
                  <SelectItem key={month.value} value={month.value}>{month.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">or</span>
            </div>
            
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters({...filters, date_from: e.target.value, month: 'all'})}
                className="w-[140px]"
              />
              <span className="text-muted-foreground">to</span>
              <Input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters({...filters, date_to: e.target.value, month: 'all'})}
                className="w-[140px]"
              />
            </div>
            
            {(filters.date_from || filters.date_to || filters.month !== 'all') && (
              <Button variant="ghost" size="sm" onClick={clearDateFilters} className="text-muted-foreground">
                <X className="h-4 w-4 mr-1" />
                Clear Dates
              </Button>
            )}
            
            {(filters.search || filters.compliance_filter !== 'all' || filters.call_type !== 'all' || 
              filters.department_id !== 'all' || filters.alert_level !== 'all' || filters.classification !== 'all' ||
              filters.qa_status !== 'all' || filters.date_from || filters.date_to || filters.month !== 'all') && (
              <Button variant="outline" size="sm" onClick={clearFilters} className="ml-auto">
                <X className="h-4 w-4 mr-1" />
                Clear All Filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions Toolbar */}
      {selectedCalls.length > 0 && (
        <div className="flex items-center justify-between gap-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="flex items-center gap-3">
            <Checkbox
              checked={selectedCalls.length === filteredCalls.length && filteredCalls.length > 0}
              onCheckedChange={handleSelectAll}
            />
            <span className="font-medium text-blue-900 dark:text-blue-100">
              {selectedCalls.length} call{selectedCalls.length > 1 ? 's' : ''} selected
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Bulk Override */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="default" className="gap-2" disabled={bulkActionLoading}>
                  {bulkActionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Edit className="h-4 w-4" />}
                  Override
                  <ChevronRight className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem 
                  onClick={() => setBulkOverrideDialogOpen(true)}
                  className="gap-2"
                >
                  <ThumbsUp className="h-4 w-4 text-green-600" />
                  Mark as Compliant
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => setBulkOverrideDialogOpen(true)}
                  className="gap-2"
                >
                  <ThumbsDown className="h-4 w-4 text-red-600" />
                  Mark as Non-Compliant
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={() => setBulkOverrideDialogOpen(true)}
                  className="gap-2"
                >
                  <Edit className="h-4 w-4" />
                  Custom Override...
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            {/* Bulk QA Review */}
            <Button 
              size="sm" 
              variant="outline" 
              className="gap-2 bg-emerald-50 dark:bg-emerald-900/20 border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/30" 
              disabled={bulkActionLoading}
              onClick={() => setBulkQAReviewDialogOpen(true)}
              data-testid="bulk-qa-review-btn"
            >
              <ClipboardCheck className="h-4 w-4" />
              QA Review
            </Button>
            
            {/* Clear Selection */}
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={() => setSelectedCalls([])}
              className="text-muted-foreground"
            >
              <X className="h-4 w-4 mr-1" />
              Clear
            </Button>
          </div>
        </div>
      )}

      {/* Tabs with Alert Lists */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="grid grid-cols-6 w-full max-w-4xl">
          <TabsTrigger value="critical" className="gap-1 text-xs sm:text-sm">
            <AlertOctagon className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">Critical</span> ({categoryStats.critical.total})
          </TabsTrigger>
          <TabsTrigger value="medium" className="gap-1 text-xs sm:text-sm">
            <AlertTriangle className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">Medium</span> ({categoryStats.medium.total})
          </TabsTrigger>
          <TabsTrigger value="improvement" className="gap-1 text-xs sm:text-sm">
            <AlertCircle className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">Improve</span> ({categoryStats.improvement.total})
          </TabsTrigger>
          <TabsTrigger value="non-compliance" className="gap-1 text-xs sm:text-sm">
            <ShieldX className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">Non-Comp</span> ({categoryStats.non_compliance.total})
          </TabsTrigger>
          <TabsTrigger value="qa-reviewed" className="gap-1 text-xs sm:text-sm">
            <ClipboardCheck className="h-3 w-3 sm:h-4 sm:w-4 text-blue-500" />
            <span className="hidden sm:inline">QA Reviewed</span> ({qaReviewedTotal})
          </TabsTrigger>
          <TabsTrigger value="marked-compliant" className="gap-1 text-xs sm:text-sm">
            <ShieldCheck className="h-3 w-3 sm:h-4 sm:w-4 text-emerald-500" />
            <span className="hidden sm:inline">Compliant</span> ({markedCompliantTotal})
          </TabsTrigger>
        </TabsList>

        {/* Calls Table - Same for all tabs */}
        <div className="mt-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center justify-between">
                <span className="capitalize">{activeTab.replace('-', ' ')} Calls</span>
                <Badge variant="secondary">Page {currentPage} of {totalPages} ({filteredCalls.length} shown)</Badge>
              </CardTitle>
              <CardDescription>
                {activeTab === 'critical' && 'Calls requiring immediate attention due to severe compliance issues'}
                {activeTab === 'medium' && 'Calls that need review within 24 hours'}
                {activeTab === 'improvement' && 'Calls where coaching is recommended to improve performance'}
                {activeTab === 'non-compliance' && 'All calls classified as High Alert due to compliance failures'}
                {activeTab === 'qa-reviewed' && 'Calls that have been reviewed by QA auditors - can be reverted if needed'}
                {activeTab === 'marked-compliant' && 'Calls marked as compliant or with compliance overridden - can be reverted if needed'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {filteredCalls.length > 0 ? (
                <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          checked={selectedCalls.length === filteredCalls.length && filteredCalls.length > 0}
                          onCheckedChange={handleSelectAll}
                        />
                      </TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Call Type</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Score</TableHead>
                      <TableHead>Classification</TableHead>
                      <TableHead>QA Status</TableHead>
                      <TableHead>Issues</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCalls.map((call) => (
                      <TableRow 
                        key={call.id} 
                        className={`${
                          call.alert_level === 'critical' ? 'bg-red-500/5 hover:bg-red-500/10' : 
                          call.alert_level === 'medium' ? 'bg-amber-500/5 hover:bg-amber-500/10' : 
                          activeTab === 'non-compliance' ? 'bg-purple-500/5 hover:bg-purple-500/10' :
                          'bg-yellow-500/5 hover:bg-yellow-500/10'
                        } ${selectedCalls.includes(call.id) ? 'ring-2 ring-blue-500/50' : ''}`}
                      >
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedCalls.includes(call.id)}
                            onCheckedChange={(checked) => handleSelectCall(call.id, checked)}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="h-8 w-8">
                              <AvatarImage src={call.user?.avatar_url} />
                              <AvatarFallback className="bg-muted text-xs">
                                {call.user?.name?.charAt(0) || '?'}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium text-sm text-foreground">{call.user?.name || 'Unassigned'}</p>
                              <p className="text-xs text-muted-foreground font-mono">{call.phone_number}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <span className="text-sm text-foreground">{formatDate(call.call_date)}</span>
                            <p className="text-xs text-muted-foreground">{formatTime(call.call_date)}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">{call.call_type}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            <span className="text-sm">{formatDuration(call.duration)}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Star className="h-4 w-4 text-amber-500" />
                            <span className={`font-semibold ${
                              call.average_score < 5 ? 'text-red-500' : 
                              call.average_score < 7 ? 'text-amber-500' : 'text-foreground'
                            }`}>
                              {call.average_score?.toFixed(1)}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={`classification-${call.classification} capitalize`}>
                            {call.classification?.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant={call.qa_reviewed || call.qa_status === 'qa_reviewed' ? 'default' : 'secondary'}
                            className={call.qa_reviewed || call.qa_status === 'qa_reviewed' 
                              ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border-emerald-500/30' 
                              : ''}
                          >
                            {call.qa_reviewed || call.qa_status === 'qa_reviewed' ? 'Reviewed' : 'Not Reviewed'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {call.compliance_alerts?.slice(0, 2).map((alert, idx) => (
                              <Badge key={idx} variant="destructive" className="text-xs">
                                {alert.type?.replace('_', ' ')}
                              </Badge>
                            ))}
                            {(call.compliance_alerts?.length || 0) > 2 && (
                              <Badge variant="secondary" className="text-xs">
                                +{call.compliance_alerts.length - 2}
                              </Badge>
                            )}
                            {!call.has_notes && (
                              <Badge variant="outline" className="text-xs border-red-500/50 text-red-500">
                                No Notes
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            {/* QA Review Button */}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); handleQuickQAReview(call); }}
                              className={`h-8 px-2 ${
                                call.qa_reviewed || call.qa_status === 'qa_reviewed'
                                  ? 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-900/20'
                                  : 'text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                              }`}
                              title={call.qa_reviewed ? 'Already QA Reviewed - Add Note' : 'QA Review'}
                              data-testid={`qa-review-btn-${call.id}`}
                            >
                              <ClipboardCheck className="h-4 w-4" />
                            </Button>
                            
                            {/* Quick Mark Compliant */}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); handleQuickMarkCompliant(call); }}
                              className="h-8 px-2 text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
                              title="Quick Mark Compliant"
                            >
                              <ThumbsUp className="h-4 w-4" />
                            </Button>
                            
                            {/* Ride Back Button - Show on QA Reviewed or Marked Compliant tabs */}
                            {(activeTab === 'qa-reviewed' || activeTab === 'marked-compliant') && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  setSelectedRideBackCall(call);
                                  setRideBackDialogOpen(true);
                                }}
                                className="h-8 px-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                                title="Ride Back - Revert Status"
                              >
                                <Undo2 className="h-4 w-4" />
                              </Button>
                            )}
                            
                            {/* More Actions */}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 px-2" onClick={(e) => e.stopPropagation()}>
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleQuickQAReview(call)}>
                                  <ClipboardCheck className="h-4 w-4 mr-2 text-emerald-600" />
                                  QA Review
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => { setSelectedCall(call); setOverrideDialogOpen(true); }}>
                                  <Edit className="h-4 w-4 mr-2" />
                                  Override Compliance
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleQuickMarkCompliant(call)}>
                                  <ThumbsUp className="h-4 w-4 mr-2 text-green-600" />
                                  Quick Mark Compliant
                                </DropdownMenuItem>
                                
                                {/* Ride Back option in dropdown */}
                                {(call.qa_reviewed || call.marked_compliant || call.compliance_overridden) && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem 
                                      onClick={() => { 
                                        setSelectedRideBackCall(call);
                                        setRideBackDialogOpen(true);
                                      }}
                                      className="text-amber-600"
                                    >
                                      <Undo2 className="h-4 w-4 mr-2" />
                                      Ride Back (Revert Status)
                                    </DropdownMenuItem>
                                  </>
                                )}
                                
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => window.open(`${baseUrl}/call/${call.id}?from=alerts`, '_blank')}>
                                  <Eye className="h-4 w-4 mr-2" />
                                  View Call Details
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                            
                            {/* Navigate to call detail - opens in new tab */}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => window.open(`${baseUrl}/call/${call.id}?from=alerts`, '_blank')}
                              className="h-8 px-2"
                              title="Open in new tab"
                            >
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                
                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <span className="text-sm text-muted-foreground">
                      Showing page {currentPage} of {totalPages}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1 || isLoading}
                      >
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        Previous
                      </Button>
                      <div className="flex items-center gap-1">
                        {[...Array(Math.min(5, totalPages))].map((_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          return (
                            <Button
                              key={pageNum}
                              variant={pageNum === currentPage ? "default" : "outline"}
                              size="sm"
                              className="w-9"
                              onClick={() => handlePageChange(pageNum)}
                              disabled={isLoading}
                            >
                              {pageNum}
                            </Button>
                          );
                        })}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages || isLoading}
                      >
                        Next
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
              ) : (
                <div className="text-center py-12">
                  <Shield className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
                  <p className="text-foreground font-medium">No alerts in this category</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {filters.search || filters.compliance_filter !== 'all' || filters.qa_status !== 'all'
                      ? 'Try adjusting your filters' 
                      : 'All calls are within acceptable parameters'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </Tabs>

      {/* Quick Action: View All in Calls Page */}
      <div className="flex justify-center">
        <Button variant="outline" onClick={() => navigate(`${baseUrl}/calls`)}>
          <Phone className="h-4 w-4 mr-2" />
          View All Calls with Advanced Filters
        </Button>
      </div>

      {/* Single Override Dialog */}
      <CallOverrideDialog
        open={overrideDialogOpen}
        onClose={() => { setOverrideDialogOpen(false); setSelectedCall(null); }}
        call={selectedCall}
        onSubmit={handleSingleOverride}
      />

      {/* Bulk Override Dialog */}
      <BulkOverrideDialog
        open={bulkOverrideDialogOpen}
        onClose={() => setBulkOverrideDialogOpen(false)}
        selectedCount={selectedCalls.length}
        onConfirm={handleBulkOverride}
      />

      {/* Single QA Review Dialog */}
      <QAReviewDialog
        open={qaReviewDialogOpen}
        onClose={() => { setQaReviewDialogOpen(false); setSelectedQAReviewCall(null); }}
        call={selectedQAReviewCall}
        onSubmit={handleQAReview}
      />

      {/* Bulk QA Review Dialog */}
      <BulkQAReviewDialog
        open={bulkQAReviewDialogOpen}
        onClose={() => setBulkQAReviewDialogOpen(false)}
        selectedCount={selectedCalls.length}
        onConfirm={handleBulkQAReview}
      />

      {/* Activity Log Dialog */}
      <Dialog open={activityLogOpen} onOpenChange={setActivityLogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="activity-log-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-blue-500" />
              QA Activity Log
            </DialogTitle>
            <DialogDescription>
              Track all QA reviews, compliance overrides, and audit actions
            </DialogDescription>
          </DialogHeader>
          
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 py-3 border-b">
            <Select 
              value={activityLogFilters.action_type || 'all'} 
              onValueChange={(v) => { 
                setActivityLogFilters(prev => ({...prev, action_type: v === 'all' ? '' : v})); 
              }}
            >
              <SelectTrigger className="w-[180px]" data-testid="activity-action-filter">
                <SelectValue placeholder="Action Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                {activityFilterOptions.action_types.map(action => (
                  <SelectItem key={action} value={action}>{formatActionType(action)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select 
              value={activityLogFilters.auditor_id || 'all'} 
              onValueChange={(v) => { 
                setActivityLogFilters(prev => ({...prev, auditor_id: v === 'all' ? '' : v})); 
              }}
            >
              <SelectTrigger className="w-[180px]" data-testid="activity-auditor-filter">
                <SelectValue placeholder="Auditor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Auditors</SelectItem>
                {activityFilterOptions.auditors.map(auditor => (
                  <SelectItem key={auditor.id || auditor.name} value={auditor.id || auditor.name}>
                    {auditor.name || auditor.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Input
              type="date"
              placeholder="From"
              value={activityLogFilters.date_from}
              onChange={(e) => setActivityLogFilters(prev => ({...prev, date_from: e.target.value}))}
              className="w-[150px]"
              data-testid="activity-date-from"
            />
            <Input
              type="date"
              placeholder="To"
              value={activityLogFilters.date_to}
              onChange={(e) => setActivityLogFilters(prev => ({...prev, date_to: e.target.value}))}
              className="w-[150px]"
              data-testid="activity-date-to"
            />
            
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => loadActivityLog(1)}
              data-testid="activity-apply-filters"
            >
              Apply Filters
            </Button>
            
            <div className="ml-auto flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleExportActivityLog}
                disabled={exportLoading}
                className="gap-2"
                data-testid="activity-export-btn"
              >
                {exportLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                Export CSV
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => loadActivityLog(activityLogPage)}
                data-testid="activity-refresh-btn"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          {/* Activity Log Table */}
          <div className="flex-1 overflow-auto">
            {activityLogLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : activityLog.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[140px]">Timestamp</TableHead>
                    <TableHead className="w-[150px]">Auditor</TableHead>
                    <TableHead className="w-[150px]">Action</TableHead>
                    <TableHead>Call / Entity</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activityLog.map((activity, idx) => (
                    <TableRow key={activity.id || idx} data-testid={`activity-row-${idx}`}>
                      <TableCell className="text-xs text-muted-foreground">
                        <div className="flex flex-col">
                          <span>{formatRelativeTime(activity.created_at)}</span>
                          <span className="text-[10px] opacity-70">
                            {activity.created_at ? new Date(activity.created_at).toLocaleString() : '-'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6">
                            <AvatarFallback className="text-xs bg-primary/10 text-primary">
                              {activity.auditor_name?.charAt(0) || 'U'}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-sm truncate max-w-[100px]" title={activity.auditor_name}>
                            {activity.auditor_name || 'Unknown'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {formatActionType(activity.action)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {activity.call_info ? (
                          <div className="flex flex-col text-xs">
                            <span className="font-medium truncate max-w-[150px]" title={activity.call_info.filename}>
                              {activity.call_info.filename || activity.entity_id}
                            </span>
                            {activity.call_info.user_name && (
                              <span className="text-muted-foreground flex items-center gap-1">
                                <User className="h-3 w-3" />
                                {activity.call_info.user_name}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">{activity.entity_id || '-'}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="text-xs space-y-1">
                          {activity.details?.new_compliance_status !== undefined && (
                            <div className="flex items-center gap-1">
                              {activity.details.new_compliance_status ? (
                                <><CheckCircle2 className="h-3 w-3 text-green-500" /> Marked Compliant</>
                              ) : (
                                <><X className="h-3 w-3 text-red-500" /> Marked Non-Compliant</>
                              )}
                            </div>
                          )}
                          {activity.details?.override_reason && (
                            <div className="text-muted-foreground truncate max-w-[200px]" title={activity.details.override_reason}>
                              Reason: {activity.details.override_reason}
                            </div>
                          )}
                          {activity.details?.qa_notes && (
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <FileText className="h-3 w-3" />
                              <span className="truncate max-w-[180px]" title={activity.details.qa_notes}>
                                {activity.details.qa_notes}
                              </span>
                            </div>
                          )}
                          {activity.details?.count && (
                            <div className="text-muted-foreground">
                              {activity.details.count} calls affected
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12">
                <History className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                <p className="text-muted-foreground">No activity logs found</p>
                <p className="text-sm text-muted-foreground mt-1">
                  QA reviews and compliance actions will appear here
                </p>
              </div>
            )}
          </div>
          
          {/* Pagination */}
          {activityLogTotalPages > 1 && (
            <div className="flex items-center justify-between pt-4 border-t">
              <div className="text-sm text-muted-foreground">
                Showing {((activityLogPage - 1) * 100) + 1} - {Math.min(activityLogPage * 100, activityLogTotal)} of {activityLogTotal} activities
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={activityLogPage <= 1 || activityLogLoading}
                  onClick={() => loadActivityLog(activityLogPage - 1)}
                  data-testid="activity-prev-page"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <span className="text-sm">
                  Page {activityLogPage} of {activityLogTotalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={activityLogPage >= activityLogTotalPages || activityLogLoading}
                  onClick={() => loadActivityLog(activityLogPage + 1)}
                  data-testid="activity-next-page"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Ride Back Dialog */}
      <RideBackDialog
        open={rideBackDialogOpen}
        onClose={() => {
          setRideBackDialogOpen(false);
          setSelectedRideBackCall(null);
        }}
        call={selectedRideBackCall}
        onSubmit={async (callId, newStatus, reason, notes) => {
          await rideBackCallStatus(callId, newStatus, reason, notes);
          // Refresh data for current tab
          await loadTabData(activeTab, currentPage);
          // Also refresh the reviewed/compliant totals
          if (activeTab === 'qa-reviewed' || activeTab === 'marked-compliant') {
            const reviewedResp = await getQAReviewedCalls({ page: 1, page_size: 1 });
            setQaReviewedTotal(reviewedResp.data.total || 0);
            const compliantResp = await getMarkedCompliantCalls({ page: 1, page_size: 1 });
            setMarkedCompliantTotal(compliantResp.data.total || 0);
          }
        }}
      />
    </div>
  );
}
